import { useState, useEffect, useCallback } from 'react';
import { ApiClient, CreationResponse } from '../../api/client';
import { UploadArea } from '../creation/UploadArea';
import { KovrificationCreationConfig } from '../strategies/KovrificationCreationConfig';
import { BestMomentsCreationConfig, BestMomentsConfigRequestData } from '../strategies/BestMomentsCreationConfig';
import { KovrificationConfig } from '../../types/creation';
import { UserInfo } from '../../types';

import { useTranslation } from '../../i18n';
import posthog from 'posthog-js';

interface DraftCreationHandlerProps {
  creation: CreationResponse;
  userInfo: UserInfo;
  creationId: string;
  strategyType: string;
  onUploadComplete: () => void;
  onPaymentComplete: (newBalance: number) => void;
  pricingInfo?: string;
}

/**
 * DraftCreationHandler component
 * 
 * This component handles the UI for draft creations with two scenarios:
 * 1. When a file is not uploaded, it shows the file upload window
 * 2. When a file is uploaded, it shows the configuration options and process button
 */
export function DraftCreationHandler({
  creation,
  userInfo,
  creationId,
  strategyType,
  onUploadComplete,
  onPaymentComplete,
  pricingInfo
}: DraftCreationHandlerProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [isPriceFetching, setIsPriceFetching] = useState(false);
  const { t } = useTranslation();
  
  // For kovrification
  const [kovrificationConfig, setKovrificationConfig] = useState<KovrificationConfig>(() => {
    // Initialize config from creation strategy
    const strategy = creation.strategy;
    return {
      video_processing: strategy.video_processing || {
        vertical_video_proportion: 0.7,
      },
      caption: {
        enabled: strategy.captions_enabled || true,
        color: strategy.caption_config?.color || '#FFFFFF',
        accent_color: strategy.caption_config?.accent_color || '#2D62FF',
        words_per_line: strategy.caption_config?.words_per_line || 3,
        vertical_margin: strategy.caption_config?.vertical_margin || 0.7,
      }
    };
  });
  
  // For best moments
  const [bestMomentsConfig, setBestMomentsConfig] = useState<BestMomentsConfigRequestData>(() => {
    // Initialize config from creation strategy
    const strategy = creation.strategy;
    return {
      captions_enabled: strategy.captions_enabled !== undefined ? strategy.captions_enabled : true,
      caption_config: {
        color: strategy.caption_config?.color || '#FFFFFF',
        accent_color: strategy.caption_config?.accent_color || '#7FFF00',
        words_per_line: strategy.caption_config?.words_per_line || 4,
        vertical_margin: strategy.caption_config?.vertical_margin || 0.7,
      }
    };
  });

  const fetchCalculatedPrice = useCallback(async () => {
    if (isPriceFetching) return;

    setIsPriceFetching(true);
    try {
      const data = await ApiClient.getCreationPrice(creationId);
      setCalculatedPrice(data.price);
    } catch (error) {
      console.error('Error fetching price:', error);
    } finally {
      setIsPriceFetching(false);
    }
  }, [creationId, isPriceFetching]);

  // Fetch the actual price when a file is uploaded for best_moments strategy
  useEffect(() => {
    if (strategyType === 'best_moments' && creation.uploaded_files.length > 0 && !calculatedPrice && !isPriceFetching) {
      fetchCalculatedPrice();
    }
  }, [strategyType, creation.uploaded_files.length, calculatedPrice, isPriceFetching, fetchCalculatedPrice]);

  const handleStartProcessing = async () => {
    if (isProcessing) return;
    
    try {
      // Immediately set processing state to prevent multiple clicks
      setIsProcessing(true);
      
      // Add a small delay to ensure the UI updates before API calls
      // This makes the button state change more noticeable to users
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Save configuration based on strategy type
      if (strategyType === 'kovrification') {
        const configForBackend = {
          video_processing: kovrificationConfig.video_processing,
          caption: {
            ...kovrificationConfig.caption,
            color: kovrificationConfig.caption.color,
            accent_color: kovrificationConfig.caption.accent_color,
            words_per_line: 3 // Force 3 words per line
          }
        };

        await ApiClient.configureKovrification(creationId, configForBackend);
      } else if (strategyType === 'best_moments') {
        const configForBackend = {
          captions_enabled: bestMomentsConfig.captions_enabled,
          caption_config: bestMomentsConfig.caption_config
        };

        await ApiClient.configureBestMoments(creationId, configForBackend);
      }

      // Start processing
      await ApiClient.startProcessing(creationId);

      // PostHog: Capture processing started event
      posthog.capture('creation_started', { 
        creation_id: creationId, 
        strategy_type: strategyType
      });

      // Get updated user info to reflect new balance
      const updatedUserInfo = await ApiClient.getUserInfo();
      onPaymentComplete(updatedUserInfo.balance);
    } catch (error) {
      console.error('Failed to start processing:', error);
      // Show error message to user
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const WebApp = (window as any).Telegram?.WebApp;
        if (WebApp) {
          WebApp.showPopup({
            title: t('errors.errorTitle'),
            message: t('draftCreationHandler.processingFailedError'),
            buttons: [{ type: 'ok' }]
          });
        }
      } catch (e) {
        console.error('Error showing popup:', e);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Render strategy-specific configuration UI
  const renderStrategyConfig = () => {
    switch (strategyType) {
      case 'kovrification':
        return (
          <KovrificationCreationConfig 
            config={kovrificationConfig} 
            onConfigChange={setKovrificationConfig} 
          />
        );
      case 'best_moments':
        return (
          <BestMomentsCreationConfig 
            config={bestMomentsConfig} 
            onConfigChange={setBestMomentsConfig} 
          />
        );
      // Add more cases here as more strategies are added
      default:
        return null;
    }
  };

  // If no file is uploaded yet, show the upload area
  if (creation.uploaded_files.length === 0) {
    return (
      <UploadArea
        creationId={creationId}
        onUploadComplete={onUploadComplete}
        pricingInfo={pricingInfo}
      />
    );
  }

  // Get the uploaded file
  const uploadedFile = creation.uploaded_files[0];

  // If file is uploaded, show configuration and process button
  return (
    <div className="space-y-6">
      {/* Original Footage - Replaced VideoPlayer with <video> */}
      <div className="bg-surface-card dark:bg-surface-card-dark rounded-xl overflow-hidden shadow-sm border border-card dark:border-card-dark">
        <div className="relative aspect-video bg-media-canvas">
          <video 
            controls
            src={uploadedFile.url}
            poster={uploadedFile.url + '?t=1'} // Add poster for thumbnail
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Best Moments Explanation - only shown for best_moments strategy */}
      {strategyType === 'best_moments' && (
        <div className="p-4 rounded-xl bg-surface-interactive dark:bg-surface-interactive-dark shadow-interactive dark:border dark:border-interactive-dark">
          <h3 className="text-lg font-medium mb-2 text-content-strong">
            {t('create.types.podcast-moments.header')}
          </h3>
          <p className="text-theme-hint mb-1">
            {t('create.types.podcast-moments.description')}
          </p>
          <p className="text-sm text-theme-hint">
            {t('draftCreationHandler.bestMomentsProcessingTime')}
          </p>
        </div>
      )}

      {/* Balance Status */}
      <div className="p-4 rounded-xl bg-surface-interactive dark:bg-surface-interactive-dark shadow-interactive dark:border dark:border-interactive-dark">
        {strategyType === 'best_moments' ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-theme-text">
                <span className="material-symbols-outlined text-primary-accent">payments</span>
                <span>{t('draftCreationHandler.processingPriceLabel')}</span>
              </div>
              {isPriceFetching ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-accent border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-theme-hint">{t('draftCreationHandler.calculatingPrice')}</span>
                </div>
              ) : calculatedPrice !== null ? (
                <div className="flex items-center gap-1 font-medium">
                  <span className="text-lg">✨</span>
                  <span>{calculatedPrice}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-theme-hint">
                  <span className="text-lg">✨</span>
                  <span>{pricingInfo || t('draftCreationHandler.defaultPricingInfo')}</span>
                </div>
              )}
            </div>
            
            {userInfo.balance > 0 && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-theme-text">{t('draftCreationHandler.yourBalanceLabel')}</span>
                <div className="flex items-center gap-1 font-medium">
                  <span className="text-lg">✨</span>
                  <span>{userInfo.balance}</span>
                </div>
              </div>
            )}
            
            {calculatedPrice !== null && userInfo.balance < calculatedPrice && (
              <div className="flex items-center gap-2 text-warning mt-2">
                <span className="material-symbols-outlined">warning</span>
                <span>{t('draftCreationHandler.needMoreSparkles', { amount: calculatedPrice - userInfo.balance })}</span>
              </div>
            )}
          </div>
        ) : userInfo.balance >= creation.strategy.price ? (
          <div className="flex items-center gap-2 text-success">
            <span className="material-symbols-outlined">check_circle</span>
            <span>{t('draftCreationHandler.enoughSparkles')}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="text-body">
              {t('draftCreationHandler.processingPriceLabel')} {creation.strategy.price}
            </div>
            <div className="text-warning">
              {t('draftCreationHandler.needMoreSparkles', { amount: creation.strategy.price - userInfo.balance })}
            </div>
          </div>
        )}
      </div>

      {/* Strategy-specific configuration */}
      {renderStrategyConfig()}

      {/* Process Button */}
      <button 
        onClick={handleStartProcessing}
        disabled={
          isProcessing || 
          (strategyType === 'best_moments' && calculatedPrice !== null && userInfo.balance < calculatedPrice) ||
          (strategyType !== 'best_moments' && userInfo.balance < creation.strategy.price)
        }
        className={`w-full py-2.5 px-4 font-medium text-sm rounded-xl transition-all duration-300 
          bg-button-primary text-on-button-primary hover:bg-button-primary-hover 
          disabled:bg-button-primary-disabled disabled:text-on-button-primary-disabled disabled:shadow-none disabled:cursor-not-allowed 
          ${isProcessing ? 'opacity-75 scale-[0.98] cursor-not-allowed' : ''}`}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center">
            <span className="w-5 h-5 border-3 border-on-button-primary border-t-transparent rounded-full animate-spin mr-3"></span>
            <span className="animate-pulse">{t('draftCreationHandler.processingButton')}</span>
          </span>
        ) : strategyType === 'best_moments' ? (
          calculatedPrice !== null && userInfo.balance < calculatedPrice ? 
            t('draftCreationHandler.insufficientBalanceButton') : t('draftCreationHandler.processVideoButton')
        ) : userInfo.balance < creation.strategy.price ? (
          t('draftCreationHandler.insufficientBalanceButton')
        ) : (
          t('create.buttons.continue')
        )}
      </button>
    </div>
  );
} 